import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import TreeSelect from 'antd/lib/tree-select';

import { useRouter } from 'utils';
import useBoundariesSelectService from '../service';
import TreeShapes from './TreeShapes';
import { StyledTreeSelect } from './styles';

const { SHOW_PARENT } = TreeSelect;

const getShapesIdsToUpdate = (shapesToUpdate, shapeGroups) => {
  const shapesFromGroup = shapesToUpdate.reduce((initialShapes, shape) => {
    const shapes = initialShapes;

    const isShapeGroup = shape.value.includes('group');
    if (isShapeGroup) {
      const shapeId = shape.value.split('-')[0];
      const shapeGroup = find(shapeGroups, { id: shapeId });

      shapes.push(...shapeGroup.shapes);
    } else {
      shapes.push({ id: shape.value });
    }

    return shapes;
  }, []);

  return shapesFromGroup.map(({ id }) => parseInt(id, 10));
};

const TreeSelectShapes = ({ shapeGroups, mapShapes }) => {
  const treeData = useMemo(() => TreeShapes.normalizeTreeData(shapeGroups), [shapeGroups]);
  const initialShapes = useMemo(() => TreeShapes.normalizeShapeFromProps(mapShapes), [mapShapes]);

  const [treeSelectShapes, changeTreeSelectShapes] = useState(() => initialShapes);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { pageId } = useRouter();
  const { MapShapesSet: setShapesIds } = useBoundariesSelectService(pageId);

  const persistShapes = (boundaries) => {
    const currentShapeIds = getShapesIdsToUpdate(boundaries, shapeGroups).sort((a, b) => a - b);
    const initialShapeIds = getShapesIdsToUpdate(initialShapes, shapeGroups).sort((a, b) => a - b);

    if (!isEqual(currentShapeIds, initialShapeIds)) {
      setShapesIds(currentShapeIds);
    }
  };

  const handleOnChangeTreeSelect = (boundaries) => {
    changeTreeSelectShapes(boundaries);
  };

  const handleDropdownVisibleChange = (isOpen) => {
    setIsDropdownOpen(isOpen);

    if (isOpen) {
      return;
    }

    persistShapes(treeSelectShapes);
  };

  // consistent shapes for tree component state
  useEffect(() => {
    changeTreeSelectShapes(initialShapes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapShapes]);

  useEffect(() => {
    if (!isDropdownOpen) {
      persistShapes(treeSelectShapes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeSelectShapes, isDropdownOpen]);

  return (
    <StyledTreeSelect
      treeCheckable
      labelInValue
      searchPlaceholder="Select..."
      showCheckedStrategy={SHOW_PARENT}
      treeNodeFilterProp="title"
      treeData={treeData}
      value={treeSelectShapes}
      defaultValue={initialShapes}
      onChange={handleOnChangeTreeSelect}
      onDropdownVisibleChange={handleDropdownVisibleChange}
    />
  );
};

TreeSelectShapes.propTypes = {
  shapeGroups: PropTypes.array,
  mapShapes: PropTypes.array,
};

export default React.memo(
  TreeSelectShapes,
  (prev, next) => isEqual(prev.mapShapes, next.mapShapes),
);
